import React, { useRef } from 'react';
import { Upload, Trash2, FileText, FileCheck, UploadCloud } from 'lucide-react';
import DocUploadProgress from './DocUploadProgress';

// Verification screen: lets the verifier upload documents the employee did not upload at registration.
// docTypes  - every document type (required ones first); types already on the employee are hidden
// existing  - DocNames the employee already has
// required  - DocNames that are mandatory
// docUploads - the useStaffDocUploads() object of the page
const MissingDocRow = ({ docName, isRequired, upload, onPick, onRemove, onRetry, disabled }) => {
    const fileRef = useRef();
    const busy = upload?.status === 'uploading';
    return (
        <div className={`flex items-center gap-3 p-3 rounded-lg border transition-colors
            ${upload?.status === 'done' ? 'border-green-200 bg-green-50/60 dark:border-green-800 dark:bg-green-900/10'
                : upload?.status === 'error' ? 'border-rose-300 bg-rose-50/60 dark:border-rose-700 dark:bg-rose-900/10'
                : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-700'}`}>
            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/20 shrink-0">
                {upload?.status === 'done'
                    ? <FileCheck className="w-4 h-4 text-green-600" />
                    : <FileText className="w-4 h-4 text-indigo-600" />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{docName}</span>
                    {isRequired && (
                        <span className="text-[9px] font-bold uppercase bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300 px-1.5 py-0.5 rounded">Required</span>
                    )}
                </div>
                {upload?.file
                    ? <p className="text-[10px] text-gray-500 truncate">{upload.file.name} ({(upload.file.size / 1024).toFixed(0)} KB)</p>
                    : <p className="text-[10px] text-gray-400">Not uploaded</p>}
                <DocUploadProgress upload={upload} onRetry={() => onRetry(docName)} />
            </div>
            {upload && !busy && (
                <button type="button" onClick={() => onRemove(docName)} disabled={disabled} title="Remove"
                    className="p-1.5 rounded text-rose-500 hover:bg-rose-100 dark:hover:bg-rose-900/30 disabled:opacity-40">
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
            <button type="button" onClick={() => fileRef.current?.click()} disabled={disabled || busy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50">
                <Upload className="w-3 h-3" /> {upload ? 'Replace' : 'Upload'}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,.pdf" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) onPick(docName, f); e.target.value = ''; }} />
        </div>
    );
};

const MissingDocsUploader = ({ docTypes, existing, required, docUploads, disabled }) => {
    const have = new Set(existing);
    const missing = docTypes.filter(d => !have.has(d));
    if (missing.length === 0) return null;
    const missingRequired = missing.filter(d => required.includes(d) && docUploads.uploads[d]?.status !== 'done');

    return (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-5 rounded-xl border border-indigo-200 dark:border-indigo-700">
            <h4 className="font-semibold text-indigo-800 dark:text-indigo-300 mb-1 flex items-center">
                <UploadCloud className="w-5 h-5 mr-2" /> Upload Missing Documents
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Documents not uploaded at registration. Each file is uploaded at once; wait for
                "Uploaded and verified" before you submit. Saved with your action (not on Reject).
            </p>
            {missingRequired.length > 0 && (
                <p className="text-xs font-semibold text-rose-600 dark:text-rose-400 mb-3">
                    Required documents still missing: {missingRequired.join(', ')}
                </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {missing.map(docName => (
                    <MissingDocRow key={docName} docName={docName} isRequired={required.includes(docName)}
                        upload={docUploads.uploads[docName]} onPick={docUploads.start} onRemove={docUploads.remove}
                        onRetry={docUploads.retry} disabled={disabled} />
                ))}
            </div>
            <p className="text-center text-[10px] text-gray-400 mt-3">Accepted formats: JPG, PNG, PDF • Max size: 5 MB per file</p>
        </div>
    );
};

export default MissingDocsUploader;
